// src/pages/ServicePages/VipSocialsAndLifestyleEventsPage.jsx

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ContactWidget from '../../components/ui/ContactWidget';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ClientLogos from 'pages/homepage/components/ClientLogos';
import VipSocialsModal from '../../components/ui/VipSocialsModal.jsx'; // <-- IMPORT THE NEW MODAL

// Reusable Gallery Image Component
const GalleryImage = ({ src, alt }) => (
  <div className="overflow-hidden rounded-xl group shadow-sm hover:shadow-lg transition-shadow duration-300">
    <img src={src} alt={alt} className="w-full h-full object-cover aspect-[4/3] group-hover:scale-105 transition-transform duration-500 ease-in-out" loading="lazy" />
  </div>
);

// Reusable FAQ Item Component
const FaqItem = ({ question, children }) => (
  <details className="p-6 rounded-lg bg-white border border-gray-200/80 cursor-pointer group transition-colors hover:bg-gray-50">
    <summary className="font-semibold text-lg flex items-center justify-between">{question}<Icon name="ChevronDown" className="h-5 w-5 transition-transform duration-300 group-open:rotate-180" /></summary>
    <div className="text-gray-600 mt-4 leading-relaxed">{children}</div>
  </details>
);

const VipSocialsAndLifestyleEventsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const galleryItems = [
    { src: '/gallery/vip/luxury-car-launch.jpg', alt: 'A luxury car launch event in Nairobi with dramatic lighting.' },
    { src: 'gallery/vip/influencer-dinner.jpg', alt: 'An exclusive influencer dinner with sophisticated tablescapes.' },
    { src: 'gallery/vip/fashion-show-runway.jpg', alt: 'A high-fashion runway show planned and executed in Kenya.' },
    { src: 'gallery/vip/charity-gala.jpg', alt: 'Guests at a high-profile charity gala in Nairobi.' },
    { src: 'gallery/vip/art-exhibition-opening.jpg', alt: 'An elegant art exhibition opening night.' },
    { src: 'gallery/vip/exclusive-lounge.jpg', alt: 'A custom-built VIP lounge at a major sporting event.' },
    { src: 'gallery/vip/champagne-reception.jpg', alt: 'Guests enjoying a champagne reception at a luxury brand activation.' },
    { src: 'gallery/vip/media-event-backdrop.jpg', alt: 'A branded media wall and backdrop for a press event.' },
  ];
  
  // SEO: Structured Data
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Luxe & Allure VIP & Luxury Event Planning",
    "image": "https://www.luxeandallureevents.co.ke/gallery/vip.jpg", // Replace with absolute URL
    "@id": "https://www.luxeandallureevents.co.ke/services/vip-socials-lifestyle-events", // Replace with absolute URL
    "url": "https://www.luxeandallureevents.co.ke/services/vip-socials-lifestyle-events", // Replace with absolute URL
    "telephone": "+254727937010",
    "priceRange": "$$$$",
    "description": "Elite Decor providers for VIP socials, luxury brand activations, and high-profile lifestyle events in Nairobi, Kenya. We create exclusive, high-impact experiences.",
    "serviceType": "Event Decor",
    "areaServed": { "@type": "City", "name": "Nairobi" }
  };

  return (
    <>
      <div className="min-h-screen bg-slate-50 text-gray-800">
        <Helmet>
          <title>VIP & Luxury Event Planners in Nairobi, Kenya | Luxe & Allure</title>
          <meta name="description" content="Nairobi's elite event planners for VIP socials, luxury brand activations, and lifestyle events. We create exclusive, high-impact experiences that build prestige and generate buzz." />
          <link rel="canonical" href="https://www.luxeandallureevents.co.ke/services/vip-socials-lifestyle-events" />
          <meta name="keywords" content="vip event planners nairobi, luxury event planners kenya, brand activation company nairobi, influencer events kenya, high-profile events, charity gala planners" />
          <meta property="og:title" content="VIP & Luxury Event Planners in Nairobi, Kenya | Luxe & Allure" />
          <meta property="og:description" content="Nairobi's elite event planners for VIP socials, luxury brand activations, and lifestyle events. We create exclusive, high-impact experiences." />
          <meta property="og:image" content="https://www.luxeandallureevents.co.ke/gallery/vip/influencer-dinner.jpg" />
          <meta property="og:url" content="https://www.luxeandallureevents.co.ke/services/vip-socials-lifestyle-events" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        </Helmet>

        <Header />
        <ContactWidget />

        <main className="font-sans">
          {/* --- Hero Section --- */}
          <section className="bg-white">
            <div className="container mx-auto px-6 lg:px-8 py-20 lg:py-24">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div className="text-center lg:text-left">
                        <Breadcrumb />
                        <h1 className="font-serif text-5xl md:text-6xl font-bold leading-tight tracking-tight mt-4">
                            Elite VIP & Luxury Event Planners in Nairobi
                        </h1>
                        <p className="mt-6 text-lg text-gray-600 max-w-xl mx-auto lg:mx-0">
                            We curate exclusive, high-impact experiences for the world's most discerning brands and clientele.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                            <Button variant="primary" size="lg" onClick={() => setIsModalOpen(true)}>
                                Schedule a Briefing
                            </Button>
                            <Button variant="outline" size="lg" onClick={() => document.getElementById('gallery-section')?.scrollIntoView({ behavior: 'smooth' })}>
                                View Our Events
                                <Icon name="ArrowDown" className="ml-2 w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                    <div className="h-80 lg:h-full w-full">
                        <img src="/gallery/vip.jpg" alt="A sophisticated and exclusive VIP social event in Nairobi." className="w-full h-full object-cover rounded-xl shadow-lg" />
                    </div>
                </div>
            </div>
          </section>

          {/* --- Philosophy Section --- */}
          <section className="py-20 lg:py-28">
            <div className="container mx-auto px-6 lg:px-8 text-center max-w-4xl">
              <h2 className="font-serif text-4xl font-bold text-gray-900">
                Where Prestige Meets Performance
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                In the world of luxury, an event is the ultimate brand statement. It’s a carefully orchestrated moment to captivate an influential audience, build prestige, and create significant media buzz. Luxe & Allure specializes in designing these "money-can't-buy" experiences, translating the nuanced language of luxury into unforgettable, high-impact events defined by sophistication and flawless execution.
              </p>
            </div>
          </section>

          {/* --- Service Types Section --- */}
          <section className="py-20 lg:py-28 bg-white">
            <div className="container mx-auto px-6 lg:px-8">
              <div className="text-center mb-16">
                  <h2 className="font-serif text-4xl font-bold text-gray-900">A Portfolio of Elite Services</h2>
                  <p className="mt-4 text-lg text-gray-600">Our expertise is in crafting exclusive events that command attention.</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                  <div className="text-center p-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Icon name="ShoppingBag" size={28} className="text-primary"/></div>
                      <h3 className="font-bold text-xl mb-2">Luxury Brand Activations</h3>
                      <p className="text-gray-600">Creating immersive pop-ups, product launches, and previews that connect with high-value customers.</p>
                  </div>
                  <div className="text-center p-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Icon name="AtSign" size={28} className="text-primary"/></div>
                      <h3 className="font-bold text-xl mb-2">Influencer & Media Events</h3>
                      <p className="text-gray-600">Curating exclusive events designed to generate authentic content and maximize press coverage.</p>
                  </div>
                  <div className="text-center p-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Icon name="Gem" size={28} className="text-primary"/></div>
                      <h3 className="font-bold text-xl mb-2">Charity Galas & Fundraisers</h3>
                      <p className="text-gray-600">Planning high-profile galas that attract notable guests and surpass fundraising goals with elegance.</p>
                  </div>
                  <div className="text-center p-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Icon name="Lock" size={28} className="text-primary"/></div>
                      <h3 className="font-bold text-xl mb-2">Private VIP Engagements</h3>
                      <p className="text-gray-600">Executing discreet dinners, art openings, and private soirées with the utmost confidentiality.</p>
                  </div>
              </div>
            </div>
          </section>

          {/* --- Gallery Section --- */}
          <section id="gallery-section" className="py-20 lg:py-28">
            <div className="container mx-auto px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="font-serif text-4xl font-bold text-gray-900">
                  A Showcase of Signature Events
                </h2>
                <p className="mt-4 text-lg text-gray-600">Explore the exclusive experiences we have brought to life.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {galleryItems.map((item, index) => ( <GalleryImage key={index} src={item.src} alt={item.alt} /> ))}
              </div>
            </div>
          </section>

          {/* --- FAQ Section --- */}
          <section className="py-20 lg:py-28 bg-white">
            <div className="container mx-auto px-6 lg:px-8">
                <div className="text-center mb-16 max-w-3xl mx-auto">
                    <h2 className="font-serif text-4xl font-bold text-gray-900">VIP & Lifestyle Event FAQs</h2>
                    <p className="mt-4 text-lg text-gray-600">Addressing the unique requirements of high-profile event planning.</p>
                </div>
                <div className="max-w-4xl mx-auto space-y-4">
                    <FaqItem question="How do you ensure privacy and security for high-profile guests?">
                      <p>Security and discretion are paramount. We collaborate with top-tier professional security firms to conduct risk assessments and develop a comprehensive security plan. This includes managing guest lists with absolute confidentiality, coordinating with personal security details, arranging secure transport, and ensuring private, discreet venue entrances and exits.</p>
                    </FaqItem>
                    <FaqItem question="Can you assist in generating media coverage for our event?">
                      <p>Yes. We work hand-in-hand with your in-house marketing team or external PR agency to maximize media impact. Our role includes managing media invitations, creating on-brand press materials, and designing "Instagrammable" moments and exclusive photo opportunities to encourage organic social sharing and targeted press coverage.</p>
                    </FaqItem>
                    <FaqItem question="How do you manage guest lists and RSVPs for exclusive events?">
                      <p>We utilize a sophisticated guest list management system to handle invitations, track RSVPs, and manage special requests with precision and grace. Our concierge-style approach ensures every high-profile guest feels acknowledged and personally attended to from the moment they receive their invitation.</p>
                    </FaqItem>
                </div>
            </div>
          </section>

          {/* --- Final Call to Action --- */}
          <section className="bg-primary text-white">
            <div className="container mx-auto px-6 lg:px-8 py-20 lg:py-24 text-center">
              <h2 className="font-serif text-4xl font-bold">
                Craft Your Signature Event
              </h2>
              <p className="mt-4 max-w-xl mx-auto text-primary/80">
                Allow us to create an experience that defines luxury and exclusivity. Schedule a confidential briefing to discuss your vision with our senior planners.
              </p>
              <Button variant="secondary" size="xl" className="mt-8" onClick={() => setIsModalOpen(true)}>
                  Schedule a Confidential Briefing
                  <Icon name="Shield" className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </section>

          <ClientLogos />
        </main>
      </div>
      
      {isModalOpen && <VipSocialsModal onClose={() => setIsModalOpen(false)} />}
    </>
  );
};

export default VipSocialsAndLifestyleEventsPage;